import { Component, inject, Input, OnChanges } from "@angular/core";
import { ProposalHistoryDetails, ProposalHistoryItem } from "@app/core/domain/proposal-history";
import { CaciUtil } from "@app/core/util/caci-util";
import { LanguageService } from "@app/shared/services/language.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { DownloadFileComponent } from "@shared/components/download-file/download-file.component";
import {
  GenericDataListItem,
  GenericDataTableComponent,
} from "@shared/components/generic-data-table/generic-data-table.component";

@Component({
  selector: "pa-history-details",
  templateUrl: "./history-details.component.html",
  styleUrls: ["../../../components/generic-data-table/generic-data-table.component.scss"],
  imports: [GenericDataTableComponent, DownloadFileComponent, TranslatePipe],
})
export class HistoryDetailsComponent implements OnChanges {
  @Input() historyItem!: ProposalHistoryItem;
  @Input() historyDetails!: ProposalHistoryDetails;

  dataList: GenericDataListItem[] = [];
  private readonly translate = inject(TranslateService);
  private readonly languageService = inject(LanguageService);

  ngOnChanges(): void {
    this.dataList = this.buildList();
  }
  getStatusText(): string {
    return `${this.historyItem.statusDescription} (${this.historyDetails.decisionDate})`;
  }

  private buildList(): GenericDataListItem[] {
    const list: GenericDataListItem[] = [];
    const languageCode = this.languageService.getLanguage();

    list.push({
      label: this.historyDetails.electives.examComponentName,
      value: "",
      whitespaceBottom: true,
    });

    list.push({
      label: `${this.translate.instant("submitProposal.minCreditsElectives")}:`,
      value: CaciUtil.getStudyPointsDisplay(this.historyDetails.electives.minimumPoints, languageCode),
      whitespaceLeft: true,
    });
    list.push({
      label: `${this.translate.instant("submitProposal.selectedCreditsElectives")}:`,
      value: CaciUtil.getStudyPointsDisplay(this.historyDetails.electives.pointsSelected, languageCode),
      whitespaceLeft: true,
    });

    if (this.historyDetails.electives.pointsPerCategory.length) {
      this.historyDetails.electives.pointsPerCategory.forEach((category, index) => {
        list.push({
          label: `${category.categoryDescription}:`,
          value: `${CaciUtil.getStudyPointsDisplay(category.pointsSelected, languageCode)}`,
          whitespaceLeft: true,
          ...(index === 0 ? { whitespaceTop: true } : {}),
        });
      });
    }

    list.push({
      label: `${this.translate.instant("schedule.history.assessmentMoment")}:`,
      value: this.historyDetails.reviewMomentDescription,
      whitespaceTop: true,
    });
    list.push({
      label: `${this.translate.instant("label.status")}:`,
      value: this.getStatusText(),
    });
    if (this.historyDetails.studentExplanation) {
      list.push({
        valueOnNewLine: true,
        label: `${this.translate.instant("schedule.history.explanationStudent")} ${
          this.historyDetails.submitDate ? `(${this.historyDetails.submitDate})` : ""
        }:`,
        value: this.historyDetails.studentExplanation,
        whitespaceTop: true,
      });
    }
    if (this.historyDetails.adviceExplanation) {
      list.push({
        valueOnNewLine: true,
        label: `${this.translate.instant("schedule.adviceGivenBy")}: ${this.historyDetails.advisor ?? ""}`,
        value: this.historyDetails.adviceExplanation,
        whitespaceTop: true,
      });
    }
    if (this.historyDetails.decisionExplanation) {
      list.push({
        valueOnNewLine: true,
        label: `${this.translate.instant("schedule.decisionMadeBy")}: ${this.historyDetails.decisionMaker ?? ""}`,
        value: this.historyDetails.decisionExplanation,
        whitespaceTop: true,
      });
    }

    return list;
  }
}
